import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Block as BlockIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { APIKeyOut, APIKeyRegenerateResponse } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

const ApiKeys: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<APIKeyOut[]>([]);
    const [loading, setLoading] = useState(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    // Dialog states
    const [regenerateDialog, setRegenerateDialog] = useState<{ open: boolean, apiKeyId: string | null }>({ open: false, apiKeyId: null });
    const [newKeyData, setNewKeyData] = useState<APIKeyRegenerateResponse | null>(null);
    const [revokeDialog, setRevokeDialog] = useState<{ open: boolean, apiKeyId: string | null }>({ open: false, apiKeyId: null });
    const [actionLoading, setActionLoading] = useState(false);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            // @ts-ignore - The generated client might not have updated types yet or strict mode issue
            const response = await clientService.api.listApiKeysApiV1ApiKeysGet();
            if (response.ok) {
                // Handle both array (old) and object (new) response formats for safety
                const data = response.data as any;
                if (Array.isArray(data)) {
                    setApiKeys(data);
                } else if (data && Array.isArray(data.api_keys)) {
                    setApiKeys(data.api_keys);
                } else {
                    console.error('Unexpected API response format:', data);
                    setApiKeys([]);
                }
            }
        } catch (error) {
            console.error('Failed to fetch API keys:', error);
            showSnackbar({ message: 'Failed to fetch API keys', type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const handleRevoke = async () => {
        if (!revokeDialog.apiKeyId) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.revokeApiKeyApiV1ApiKeysApiKeyIdRevokePut(revokeDialog.apiKeyId);
            if (response.ok) {
                showSnackbar({ message: 'API Key revoked successfully', type: snackbarType.SUCCESS });
                fetchApiKeys();
                setRevokeDialog({ open: false, apiKeyId: null });
            }
        } catch (error) {
            console.error('Failed to revoke API key:', error);
            showSnackbar({ message: 'Failed to revoke API key', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        if (!regenerateDialog.apiKeyId) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.regenerateApiKeyApiV1ApiKeysApiKeyIdRegeneratePost(regenerateDialog.apiKeyId);
            if (response.ok) {
                setNewKeyData(response.data);
                showSnackbar({ message: 'API Key regenerated successfully', type: snackbarType.SUCCESS });
                fetchApiKeys();
            }
        } catch (error) {
            console.error('Failed to regenerate API key:', error);
            showSnackbar({ message: 'Failed to regenerate API key', type: snackbarType.ERROR });
            setRegenerateDialog({ open: false, apiKeyId: null });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseRegenerateDialog = () => {
        setRegenerateDialog({ open: false, apiKeyId: null });
        setNewKeyData(null);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        showSnackbar({ message: 'Copied to clipboard', type: snackbarType.INFO });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">API Keys</Typography>
                <Button 
                    startIcon={<RefreshIcon />} 
                    onClick={fetchApiKeys} 
                    variant="outlined"
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Key Prefix</TableCell>
                            <TableCell>User Name</TableCell>
                            <TableCell>User Email</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : apiKeys.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    No API keys found
                                </TableCell>
                            </TableRow>
                        ) : (
                            apiKeys.map((key) => (
                                <TableRow key={key.id}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{key.key_prefix}</TableCell>
                                    <TableCell>{(key as any).user_name || '-'}</TableCell>
                                    <TableCell>{(key as any).user_email || '-'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={key.status} 
                                            color={key.status === 'active' ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(key.created_at)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Regenerate Key">
                                            <IconButton 
                                                onClick={() => setRegenerateDialog({ open: true, apiKeyId: key.id })}
                                                color="primary"
                                                disabled={key.status !== 'active'}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Revoke Key">
                                            <IconButton 
                                                onClick={() => setRevokeDialog({ open: true, apiKeyId: key.id })}
                                                color="warning"
                                                disabled={key.status !== 'active'}
                                            >
                                                <BlockIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Regenerate Dialog */}
            <Dialog open={regenerateDialog.open} onClose={newKeyData ? undefined : handleCloseRegenerateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Regenerate API Key</DialogTitle>
                <DialogContent>
                    {newKeyData ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {newKeyData.message}
                            </Alert>
                            <Typography variant="subtitle2" gutterBottom>
                                New API Key (Copy this now, it won't be shown again):
                            </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                bgcolor: 'grey.100', 
                                p: 2, 
                                borderRadius: 1,
                                wordBreak: 'break-all'
                            }}>
                                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                                    {newKeyData.api_key}
                                </Typography>
                                <IconButton onClick={() => copyToClipboard(newKeyData.api_key)} size="small">
                                    <ContentCopyIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        <Typography>
                            Are you sure you want to regenerate this API key? The old key will be immediately revoked and deleted. This action cannot be undone.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {newKeyData ? (
                        <Button onClick={handleCloseRegenerateDialog} variant="contained">Close</Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseRegenerateDialog}>Cancel</Button>
                            <Button onClick={handleRegenerate} color="primary" variant="contained" disabled={actionLoading}>
                                {actionLoading ? <CircularProgress size={24} /> : 'Regenerate'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Revoke Dialog */}
            <Dialog open={revokeDialog.open} onClose={() => setRevokeDialog({ open: false, apiKeyId: null })}>
                <DialogTitle>Revoke API Key</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to revoke this API key? Applications using this key will no longer be able to access the API.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialog({ open: false, apiKeyId: null })}>Cancel</Button>
                    <Button onClick={handleRevoke} color="warning" variant="contained" disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={24} /> : 'Revoke'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiKeys;
